<template>
  <master-layout id="content-home" v-slot="slotProps">
    <div
      v-show="!loading"
      class="lg:flex-1 lg:overflow-hidden relative font-aktiv-grotesk lg:pb-[142px] bg-white"
    >
      <div class="w-full">
        <LogoutPopup :show="showModal" @close-modal="closeModal"></LogoutPopup>
        <div class="home-hero relative w-full overflow-hidden bg-white">
          <svg
            v-if="showInSeasonUi"
            class="home-hero-bg absolute left-0 top-0 w-full"
            :class="{ 'home-hero-bg-ios': isIos }"
            viewBox="0 0 430 250"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <!-- one path per layout: phone / tablet / desktop -->
            <path class="md:hidden" d="M0 0 H430 V190 Q215 140 0 190 Z" fill="#FFD900" />
            <path
              class="hidden md:block lg:hidden"
              d="M0 0 H430 V165 Q215 115 0 165 Z"
              fill="#FFD900"
            />
            <path class="hidden lg:block" d="M0 0 H430 V165 Q215 115 0 165 Z" fill="#FFD900" />
          </svg>
          <div v-else class="home-hero-bg absolute left-0 top-0 w-full h-full bg-[#FFD900]"></div>
          <div
            class="home-hero-header relative z-10 flex items-center justify-between px-[24px] lg:px-[56px]"
            :class="{ '!pt-[60px]': isIos }"
          >
            <img
              src="../../../public/assets/icons/GENERATIONS.svg"
              class="home-logo w-auto lg:hidden"
              alt="GenerationS"
            />
            <div class="home-header-actions">
              <button
                v-if="showInSeasonUi"
                class="flex-none h-11 w-11 relative rounded-full flex items-center justify-center cursor-pointer hover:opacity-90"
                data-event-tracking="gensapp-home-buttons-myqr"
                data-test-id="gensapp-home-buttons-myqr"
                @click="goToQR()"
              >
                <img
                  src="../../../public/assets/icons/home-qr-button.svg"
                  class="h-full w-full object-contain"
                  alt="My QR"
                />
              </button>
              <button
                class="flex-none h-11 w-11 relative rounded-full bg-[#00A9E0] flex items-center justify-center cursor-pointer hover:opacity-90 shadow-[0_1px_10px_rgba(0,0,0,0.1)]"
                data-event-tracking="gensapp-home-buttons-update"
                data-test-id="gensapp-home-buttons-update"
                @click="goToAnnouncements()"
              >
                <img
                  src="../../../public/assets/icons/bell-icon.svg"
                  class="home-header-action-icon"
                  alt="Notifications"
                />
                <div v-if="hasUnreadAnnouncements" class="unread-count">
                  <p>{{ totalUnreadCount }}</p>
                </div>
              </button>
            </div>
          </div>
          <div
            class="home-actions relative z-10 grid justify-center"
            :class="{
              'grid-cols': isSCS && showInSeasonUi,
              'grid-cols-GC': !(isSCS && showInSeasonUi),
            }"
          >
            <div v-if="isSCS && showInSeasonUi" class="home-action-item">
              <button
                id="experienceIcon"
                class="home-action-button"
                data-event-tracking="gensapp-home-buttons-experience"
                data-test-id="gensapp-home-buttons-experience"
                @click="goToExperience()"
              >
                <img
                  src="../../../public/assets/images/experience-logo.svg"
                  class="home-action-experience-icon icon-shadow"
                  alt="Experiences"
                />
              </button>
              <div class="home-action-label">Experiences</div>
            </div>
            <div v-if="showInSeasonUi" class="home-action-item">
              <button
                id="photodropIcon"
                class="home-action-button"
                data-event-tracking="gensapp-home-buttons-photodrop"
                data-test-id="gensapp-home-buttons-photodrop"
                @click="goToPhotoDrop()"
              >
                <img
                  src="../../../public/assets/images/photodrop-logo.svg"
                  class="home-action-photodrop-icon icon-shadow"
                  alt="PhotoDrop"
                />
              </button>
              <div class="home-action-label">PhotoDrop</div>
            </div>
            <div v-if="showInSeasonUi" class="home-action-item">
              <button
                id="interpretationIcon"
                class="home-action-button icon-shadow bg-lightblue"
                data-event-tracking="gensapp-home-buttons-interpretation"
                data-test-id="gensapp-home-buttons-interpretation"
                @click="goToInterpretation()"
              >
                <img
                  src="../../../public/assets/icons/interpretation.svg"
                  class="home-action-interpretation-icon object-contain"
                  alt="Interpretation"
                />
              </button>
              <div class="home-action-label">Interpretation</div>
            </div>
          </div>
        </div>

        <div class="fixed bottom-[128px] z-50">
          <CheatConsole v-if="isNonProd"></CheatConsole>
        </div>
      </div>
      <section ref="test" class="flex flex-col">
        <div
          v-for="(component, idx) in homePageSection"
          :key="`${homePageSectionListKey}-${idx}-${component.name}`"
        >
          <div
            v-if="showCustomComponent(component.name) && showForDelegates(component.isPublic)"
            :ref="component.name"
            class="anchor-scroll-margin"
          >
            <html-to-vue :html="component.html" />
          </div>
          <CVPCard
            class="w-full anchor-scroll-margin"
            v-if="cvpObj.show && component.name === 'cvp' && showForDelegates(component.isPublic)"
            :ref="component.name"
            :link="cvpObj.url"
            :thumbnailImgMobile="cvpObj.thumbnailMobile.url"
            :thumbnailImgTablet="cvpObj.thumbnailTablet.url"
          ></CVPCard>
          <h2
            class="mx-6 md:mx-8 lg:w-[750px] lg:mx-auto mt-6 anchor-scroll-margin"
            v-if="component.name === 'schedule' && showForDelegates(component.isPublic)"
            :ref="component.name"
          >
            {{ $t('copy.homePage.mainLayout.scheduleSection.title') }}
          </h2>
          <ScheduleSection
            ref="scheduleSection"
            v-if="component.name === 'schedule' && showForDelegates(component.isPublic)"
            :events="events"
            :seatings="seatings"
            @init="init"
          ></ScheduleSection>

          <!-- Must watch list -->
          <MustwatchSection
            v-if="component.name === 'must-watch' && showForDelegates(component.isPublic)"
            :ref="component.name"
            :all-watch-lists="allWatchLists"
          />

          <Experiences
            v-if="isSCS && component.name === 'experience' && showForDelegates(component.isPublic)"
            ref="experienceSection"
            :getExpAlloc="getExpAlloc()"
            @show-popup="triggerExpPopup($event, slotProps)"
          >
          </Experiences>
          <section
            v-if="
              showRegistrationLink &&
              component.name === 'registration' &&
              showForDelegates(component.isPublic)
            "
            :ref="component.name"
            class="bg-white"
          >
            <div class="relative mt-6 mb-6 md:rounded-3xl lg:w-[750px] md:mx-auto">
              <img
                :src="registrationLink?.thumbnail.url"
                class="w-screen md:rounded-3xl lg:w-[750px]"
              />
              <div
                class="absolute bottom-[2rem] left-0 right-0 mx-auto md:mb-16 h-[48px] flex justify-center font-aktiv-grotesk-trial text-[20px] leading-[24px]"
              >
                <in-app-browser :href="`${registrationLink.url}`" target="_blank">
                  <button
                    class="px-5 py-3 bg-lightblue text-white rounded-full text-center"
                    data-event-tracking="gensapp-home-sign-up-gens-2025"
                    data-test-id="gensapp-home-sign-up-gens-2025"
                    @click="logEvent('gensapp-home-sign-up-gens-2025')"
                  >
                    {{ registrationLink.text }}
                  </button>
                </in-app-browser>
              </div>
            </div>
          </section>
          <KickOffSection
            v-if="component.name === 'kickoff' && showForDelegates(component.isPublic)"
            ref="kickOffSection"
          ></KickOffSection>
          <PlaylistSection
            v-if="component.name === 'playlist' && showForDelegates(component.isPublic)"
            :ref="component.name"
          ></PlaylistSection>
          <section
            v-if="component.name === 'link' && showForDelegates(component.isPublic)"
            :ref="component.name"
            class="flex flex-col gap-5 pt-6 pb-6 mx-6 md:mx-8 lg:w-[750px] lg:mx-auto"
          >
            <h2>{{ $t('copy.homePage.mainLayout.usefulLinks.title') }}</h2>
            <GuidedTourSection
              conferenceType="Global"
              @reset-tour="resetGuidedTour"
            ></GuidedTourSection>
            <GivingSection></GivingSection>
          </section>
        </div>
      </section>
    </div>
    <div v-show="loading" class="w-screen h-screen flex items-center justify-center">
      <loading-state />
    </div>
  </master-layout>
</template>

<script>
import moment from 'moment';
import momentts from 'moment-timezone';
import axios from 'axios';
import { mapGetters, mapState, mapActions } from 'vuex';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import LoadingState from '@/components/common/LoadingState.vue';
import CVPCard from '@/components/HomePage/CVPCard.vue';
import ScheduleSection from '@/components/HomePage/ScheduleSection.vue';
import KickOffSection from '@/components/HomePage/KickOffSection.vue';
import PlaylistSection from '@/components/HomePage/PlaylistSection.vue';
import GivingSection from '@/components/HomePage/GivingSection.vue';
import GuidedTourSection from '@/components/HomePage/GuidedTourSection.vue';
import MustwatchSection from '@/components/HomePage/MustwatchSection.vue';
import CheatConsole from '@/components/common/CheatConsole.vue';
import Experiences from '@/components/HomePage/Experiences.vue';
import LogoutPopup from '@/components/HomePage/LogoutPopup.vue';
import { isNativeNotWeb } from '@/utils/platform-check';
// NOTE: filename is interpretation-webview (not interpretation) on purpose —
// this page's template also imports interpretation.svg, and @vue/vue3-jest
// compiles both into one module scope where babel derives the SAME variable
// name (_interpretation) for each, so the svg fileMock clobbers the util in
// unit tests. Distinct basename = distinct identifier = no collision.
import {
  openInterpretationWebView,
  resolveInterpretationPage,
  resolveSrc,
} from '@/utils/interpretation-webview';
import platformMixin from '@/mixins/platformMixin';
import store from '@/store/index';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { CONFERENCE_TYPES } from '@/utils/conference-type';
import { buildPhotodropMementoUrl, hasMementoAccess } from '@/utils/memento';

export default {
  name: 'IndexPage',
  mixins: [platformMixin],
  components: {
    ScheduleSection,
    CVPCard,
    KickOffSection,
    PlaylistSection,
    GivingSection,
    GuidedTourSection,
    CheatConsole,
    LoadingState,
    LogoutPopup,
    Experiences,
    MustwatchSection,
  },
  layout: 'default',
  data() {
    return {
      CONFERENCE_TYPES,
      events: [],
      cvpObj: {},
      homePageSection: [],
      homePageSectionListKey: 0,
      seatings: [],
      showModal: false,
      tour: null,
      splashHidden: false,
      appStateListener: null,
      // Guards the openInterpretation deep-link watcher; component-local on
      // purpose — it only serializes THIS page's handler.
      interpDeepLinkInFlight: false,
    };
  },
  computed: {
    ...mapGetters('timer', ['currDate']),
    ...mapGetters('profile', ['type', 'isAbsentAlumni']),
    ...mapState('cms', ['registrationLink', 'loading']),
    ...mapState('user', ['token']),
    ...mapGetters('announcements', ['getUnreadCount', 'unreadPersonalCount']),
    ...mapState('tour', ['tour']),
    ...mapState('profile', [
      'hasCompletedHomepageTour',
      'conferences',
      'hasOldPhotodrop',
      'email',
      'country',
      'groupName',
    ]),
    // name is the raw profile state (DB name column), NOT the profile `name`
    // getter — memento must receive the full name, never the preferred name.
    ...mapState('profile', { rawName: 'name' }),
    ...mapState('filter', ['allWatchLists']),
    ...mapState('signup', ['signups', 'pending_signups', 'backFromSummary']),
    ...mapGetters('signup', ['SIGN_UP_CLOSE_TS', 'EXP_ALLOC_TS']),
    ...mapState('schedule', ['scheduleDates']),

    totalUnreadCount() {
      return this.getUnreadCount(CONFERENCE_TYPES.GLOBAL) + this.unreadPersonalCount;
    },
    hasUnreadAnnouncements() {
      return this.totalUnreadCount > 0;
    },
    isNonProd() {
      return process.env.VUE_APP_NODE_ENV !== 'production';
    },

    // True once /profile has actually populated the store. INITIAL_STATE leaves
    // conferences empty. Deliberately not falling back to confYear — that field
    // is deprecated.
    profileLoaded() {
      return this.conferences?.length > 0;
    },

    // Gates the in-season chrome (hero shape, QR / Experiences / PhotoDrop /
    // Interpretation buttons).
    //
    // Must require a loaded profile. With an empty store, isAbsentAlumni falls
    // through to `if (!confYear) return false` (conference-type.js:124) and
    // reports "not an alumnus", which renders the in-season view to off-season
    // delegates. That happens whenever the router guard swallows an auth or
    // profile error and lets navigation continue anyway (router/index.js:491) —
    // rare, self-healing on the next successful fetch, and the cause of the
    // buttons appearing intermittently off-season.
    showInSeasonUi() {
      return this.profileLoaded && !this.isAbsentAlumni;
    },
    isSCS() {
      return this.type !== 'GC';
    },
    showRegistrationLink() {
      return this.registrationLink.show;
    },
    conferencePassCode() {
      const gpcConferences = this.conferences.filter(conference => conference.type === 'GPC');
      const currentConference = gpcConferences.find(conference => conference.currentConference);
      if (currentConference) return currentConference.conferencePassCode;

      return gpcConferences.reduce((latestConference, conference) => {
        if (!latestConference || conference.year > latestConference.year) return conference;
        if (conference.year === latestConference.year && conference.id > latestConference.id) {
          return conference;
        }
        return latestConference;
      }, null)?.conferencePassCode;
    },
    closestDateStr() {
      momentts.tz.setDefault('Asia/Singapore');
      let displayStr =
        this.currDate === ''
          ? momentts().format('YYYY-MM-DD')
          : momentts(this.currDate).format('YYYY-MM-DD');
      const dateFirstStr = this.scheduleDates[0];
      const dateLastStr = this.scheduleDates[this.scheduleDates.length - 1];
      const dateFirstStrGC = this.checkWeekend(this.scheduleDates).Sat;
      const dateLastStrGC = this.checkWeekend(this.scheduleDates).Sun;

      const display = new Date(displayStr);
      const dateFirst = new Date(dateFirstStr);
      const dateLast = new Date(dateLastStr);
      const dateFirstGC = new Date(dateFirstStrGC);
      const dateLastGC = new Date(dateLastStrGC);

      if (display.getTime() < dateFirst.getTime()) displayStr = dateFirstStr;
      if (display.getTime() > dateLast.getTime()) displayStr = dateLastStr;
      if (display.getTime() < dateFirstGC.getTime() && this.type === 'GC')
        displayStr = dateFirstStrGC;
      if (display.getTime() > dateLastGC.getTime() && this.type === 'GC')
        displayStr = dateLastStrGC;
      return displayStr;
    },
  },
  watch: {
    currDate(_newCurrDate, _oldCurrDate) {
      this.init();
    },
    '$i18n.locale'(newLocale, oldLocale) {
      if (newLocale !== oldLocale) {
        this.refreshHomeData();
      }
    },
    '$route.hash': {
      handler(newHash, oldHash) {
        // This watcher triggers when the hash (e.g., #schedule, #experience) changes
        // More reliable in Ionic than beforeRouteUpdate
        console.log('Hash changed from', oldHash, 'to', newHash);

        // Scroll to the section when hash changes
        if (newHash && newHash !== oldHash) {
          this.$nextTick(() => {
            this.scrollToSlugSection();
          });
        }
      },
      immediate: false,
    },
    // Deep link into the Interpretation WebView (issue #278):
    // /home?openInterpretation=listener[&src=qr|push|cms]. immediate covers
    // cold start (query present at creation); the watcher covers warm arrivals
    // (Ionic keeps this page alive, so a new push here changes $route.query).
    '$route.query.openInterpretation': {
      immediate: true,
      async handler(value) {
        // undefined = param absent. Empty string / arrays / unknown values flow
        // through resolveInterpretationPage and fall back to 'home'.
        if (value === undefined || this.interpDeepLinkInFlight) return;
        // Ionic caches this page and $route is the GLOBAL route, so this
        // watcher also fires when some OTHER route gains the param (e.g. a
        // crafted /schedule?openInterpretation=...). Only act when the query
        // arrived on this page's own path. Normalized: vue-router's default
        // non-strict matching also renders this page for /home/ and /Home
        // (realistic in printed QR URLs).
        const arrivalPath = this.$route.path.replace(/\/+$/, '').toLowerCase();
        if (arrivalPath !== '/home') return;
        this.interpDeepLinkInFlight = true;
        try {
          const page = resolveInterpretationPage(value);
          const src = resolveSrc(this.$route.query.src ?? null);
          // Strip BEFORE opening: a crash below can then never leave a
          // self-re-triggering URL in history.
          await this.stripInterpretationParams();
          // The strip is a guarded navigation — beforeEach may redirect it.
          // Conference remap to /gpcr/home | /gic/home is documented and fine;
          // anything else (logout to /login, access-denied) must NOT get the
          // WebView opened on top of it. The query re-check also covers a
          // guard ABORT (NavigationFailure resolves, route unchanged): the
          // strip-before-open invariant must hold before anything opens.
          const landedPath = this.$route.path.replace(/\/+$/, '').toLowerCase();
          if (!landedPath.endsWith('/home')) return;
          if (this.$route.query.openInterpretation !== undefined) return;
          await openInterpretationWebView(page, {
            src,
            conferencePassCode: this.conferencePassCode,
          });
        } catch (e) {
          // Failure UX: the user stays on Home — the Interpretation button IS
          // the retry path.
          this.$store.dispatch(
            'toast/error',
            "Couldn't open interpretation. Tap the Interpretation button to try again.",
          );
          // forwardErrorsToLogs (src/utils/datadog.js) ships this to Datadog.
          console.error('interpretation deeplink failed', e);
        } finally {
          // A failed open must never block future deep links (a second tap in
          // the same session is a supported flow).
          this.interpDeepLinkInFlight = false;
          // An arrival dropped while we were in flight leaves its query in the
          // route, and an identical future link would be a duplicate navigation
          // the watcher never sees — strip it so later links stay live (drop
          // policy: no second open for the dropped arrival itself).
          if (
            this.$route.query.openInterpretation !== undefined &&
            this.$route.path.endsWith('/home')
          ) {
            try {
              await this.stripInterpretationParams();
            } catch {
              // noop
            }
          }
        }
      },
    },
  },

  async ionViewWillEnter() {
    await this.refreshHomeData();
  },

  async ionViewDidEnter() {
    if (this.appStateListener) {
      await this.appStateListener.remove();
      this.appStateListener = null;
    }
    this.appStateListener = await CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        await this.refreshHomeData();
      }
    });

    setTimeout(() => this._pickAndLoadTour(), 100);

    this.updateExperienceCarousel();

    if (this.$refs.scheduleSection?.[0]) {
      this.$refs.scheduleSection[0].setClosestEvent();
    }

    this.$nextTick(async () => {
      if (!this.splashHidden) {
        await SplashScreen.hide();
        this.splashHidden = true;
      }
      this.setLoading(false);
      setTimeout(async () => {
        await this.scrollToSlugSection();
      }, 500);
    });
    FirebaseAnalytics.setScreenName({
      screenName: 'home',
      nameOverride: 'HomePage',
    });
  },
  async ionViewDidLeave() {
    if (this.appStateListener) {
      await this.appStateListener.remove();
      this.appStateListener = null;
    }
    if (this.$refs.kickOffSection) {
      this.$refs.kickOffSection[0].pause();
    }
  },
  methods: {
    ...mapActions('tour', ['setTour']),
    ...mapActions('cms', ['setLoading']),
    async refreshHomeData() {
      await Promise.all([
        this.init(),
        this.$store.dispatch('social/fetchSocialProfile'),
        this.$store.dispatch('signup/fetchSignup'),
        this.$store.dispatch('signup/fetchDates'),
        this.$store.dispatch('announcements/fetchUnreadCount', CONFERENCE_TYPES.GLOBAL),
        this.$store.dispatch('announcements/loadPersonalNotifications'),
        this.$store.dispatch('cms/fetchAnnouncements', {
          locale: this.$i18n.locale,
          conferenceType: CONFERENCE_TYPES.GLOBAL,
          force: true,
        }),
        this.$store.dispatch('cms/fetchRegistrationLink', {
          locale: this.$i18n.locale,
          conferenceType: CONFERENCE_TYPES.GLOBAL,
        }),
        this.$store.dispatch('filter/fetchWatchLists', {
          locale: this.$i18n.locale,
          conferenceType: CONFERENCE_TYPES.GLOBAL,
        }),
        this.fetchHomePage(),
        this.fetchCVP(),
      ]);
    },
    async logEvent(event) {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: event,
          page: 'home',
        },
      });
    },
    checkWeekend(dateList) {
      let Sat,
        Sun = '';
      dateList.forEach(date => {
        if (moment(date).day() === 6) {
          Sat = date;
        } else if (moment(date).day() === 0) {
          Sun = date;
        }
      });
      return { Sat, Sun };
    },
    closeModal() {
      this.showModal = false;
    },
    goToExperience() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-experience',
          page: 'home',
        },
      });
      this.$router.push('/experience');
    },
    goToPhotoDrop() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-photodrop',
          page: 'home',
        },
      });
      // Same rule as the nav (BottomNav/SideBar). There are two possible photo
      // destinations; which one the delegate gets depends on what they have.
      const hasMemento = hasMementoAccess(this.conferences);
      const hasOldPhotodrop = this.hasOldPhotodrop;

      // Both destinations — the welcome page lets them pick.
      if (hasMemento && hasOldPhotodrop) {
        this.$router.push('/photodrop/welcome');
        return;
      }

      // Memento only — welcome page would show one button.
      if (hasMemento) {
        window.open(
          buildPhotodropMementoUrl({
            name: this.rawName,
            email: this.email,
            country: this.country,
            groupName: this.groupName,
          }),
          '_blank',
        );
        return;
      }

      // Old gallery, or nothing new to show.
      this.$router.push('/photodrop');
    },
    goToAnnouncements() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-update',
          page: 'home',
        },
      });
      this.$router.push('/announcement');
    },
    goToSchedule() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-schedule',
          page: 'home',
        },
      });
      this.$router.push('/schedule');
    },
    goToQR() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-myqr',
          page: 'home',
        },
      });
      this.$router.push('/qr-code');
    },
    // Strip this feature's deep-link params, keeping unrelated query keys and
    // the hash. One param list shared by the watcher's main flow and its
    // finally late-strip — no drift.
    stripInterpretationParams() {
      const { openInterpretation: _oi, src: _src, ...rest } = this.$route.query;
      return this.$router.replace({
        path: this.$route.path,
        query: rest,
        hash: this.$route.hash,
      });
    },
    async goToInterpretation() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'gensapp-home-buttons-interpretation',
          page: 'home',
        },
      });
      try {
        await openInterpretationWebView('home', {
          conferencePassCode: this.conferencePassCode,
        });
      } catch (e) {
        // Same failure UX as the deep-link path — this button is its retry
        // path, so it must never fail silently.
        this.$store.dispatch('toast/error', "Couldn't open interpretation. Please try again.");
        console.error('interpretation open failed', e);
      }
    },
    async init() {
      await this.fetchEvent(this.closestDateStr);
    },
    async fetchEvent(dateStr) {
      const { data } = await axios.get(process.env.VUE_APP_API_URL + '/cms/schedules/' + dateStr, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
        params: { locale: this.$i18n.locale },
      });
      this.events = data;
    },
    async fetchCVP() {
      const { data } = await axios.get(process.env.VUE_APP_API_URL + '/cms/gc-app-cvp', {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      this.cvpObj = data;
    },
    async fetchHomePage() {
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/gc-app-homepage-sections',
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
          params: { _ts: Date.now(), locale: this.$i18n.locale },
        },
      );
      const sections = Array.isArray(data) ? data : [];
      // Only re-render the homepage sections when the content actually changed.
      // Reassigning + bumping the list key forces a full re-mount of every
      // section, which collapses the layout and resets the ion-content scroll to
      // the top. On a normal re-enter (e.g. returning from the /url in-app
      // browser redirect) the CMS data is identical, so skipping the swap keeps
      // the user's scroll position.
      if (JSON.stringify(sections) !== JSON.stringify(this.homePageSection)) {
        this.homePageSection = sections;
        this.homePageSectionListKey += 1;
      }
    },
    addToBookmark() {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
      const isChrome = /Chrome/i.test(navigator.userAgent);
      const isSafari = /Safari/i.test(navigator.userAgent);
      const isFirefox = /Firefox/i.test(navigator.userAgent);
      const isEdge = /Edge/i.test(navigator.userAgent);

      if (isMobile) {
        if (isChrome) {
          return `
                    <span style="text-align:left; color:#231F20; font-size: 20px; line-height: 24px;">Add to Home Screen</span>
                    <br>
                    <span style="text-align:left; color:#231F20; font-weight: 400; font-size: 16px; line-height: 24px;">Add this app to your home screen for quick and easy access throughout the conference.</span>
                    <br>
                    <b>Step 1:</b> <i>Tap on the share button on the bottom navigation bar</i>
                    <img src="../../../assets/images/guided-tour/mobile/chrome/navbar.png" alt="Add to Home Screen Step 1" />
                    <br>
                    <b>Step 2:</b> <i>Click on "More tools"</i>
                    <img src="../../../assets/images/guided-tour/mobile/chrome/tools.png" alt="Click on More tools">
                    <br>
                    <b>Step 3:</b> <i>Click on "Create shortcut..."</i>
                    <img src="../../../assets/images/guided-tour/mobile/chrome/shortcut.png" alt="Create shortcut">
                  `;
        } else if (isSafari) {
          return `
                    <div>
                      <span style="text-align:left; color:#231F20; font-size: 20px; line-height: 24px;">Add to Home Screen</span>
                      <br>
                      <span style="text-align:left; color:#231F20; font-weight: 400; font-size: 16px; line-height: 24px;">Add this app to your home screen for quick and easy access throughout the conference.</span>
                      <br>
                      <b>Step 1:</b> <i>Tap on the share button on the bottom navigation bar</i>
                      <img src="../../../assets/images/guided-tour/mobile/safari/navbar.png" alt="Step 1" />
                      <br>
                      <b>Step 2:</b> <i>Click on “Add to Home Screen”</i>
                      <img src="../../../assets/images/guided-tour/mobile/safari/bookmark.png" alt="Step 2" />
                    </div>
                  `;
        }
      } else {
        if (isFirefox) {
          return `
                    <div>
                      <span style="text-align:left; color:#231F20; font-size: 20px; line-height: 24px;">Bookmark this app</span>
                      <br>
                      <span style="text-align:left; color:#231F20; font-weight: 400; font-size: 16px; line-height: 24px;">Bookmark this app for quick and easy access throughout the conference.</span>
                      <br>
                      <b>Step 1:</b> <i>Click on the star at the right side of the search bar</i>
                      <img src="../../../assets/images/guided-tour/desktop/firefox/navbar.png" alt="Step 1" />
                      <br>
                      <b>Step 2:</b> <i>Click on Save to add bookmark into your browser</i>
                      <img src="../../../assets/images/guided-tour/desktop/firefox/bookmark.png" alt="Step 2" />
                    </div>
                  `;
        } else if (isEdge) {
          return `
                    <div>
                      <span style="text-align:left; color:#231F20; font-size: 20px; line-height: 24px;">Bookmark this app</span>
                      <br>
                      <span style="text-align:left; color:#231F20; font-weight: 400; font-size: 16px; line-height: 24px;">Bookmark this app for quick and easy access throughout the conference.</span>
                      <br>
                      <b>Step 1:</b> <i>Click on the star at the right side of the search bar</i>
                      <img src="../../../assets/images/guided-tour/desktop/edge/navbar.png" alt="Step 1" />
                      <br>
                      <b>Step 2:</b> <i>Click on Done to add bookmark into your browser</i>
                      <img src="../../../assets/images/guided-tour/desktop/edge/bookmark.png" alt="Step 2" />
                    </div>
                  `;
        } else if (isSafari) {
          return `
                    <div>
                      <span style="text-align:left; color:#231F20; font-size: 20px; line-height: 24px;">Bookmark this app</span>
                      <br>
                      <span style="text-align:left; color:#231F20; font-weight: 400; font-size: 16px; line-height: 24px;">Bookmark this app for quick and easy access throughout the conference.</span>
                      <br>
                      <b>Step 1:</b> <i>Click on the icon on the top right corner of the browser</i>
                      <img src="../../../assets/images/guided-tour/desktop/safari/navbar.png" alt="Step 1" />
                      <br>
                      <b>Step 2:</b> <i>Click on Add Bookmark</i>
                      <img src="../../../assets/images/guided-tour/desktop/safari/bookmark.png" alt="Step 2" />
                      <br>
                      <b>Step 3:</b> <i>Click on Add to add bookmark into your browser"</i>
                      <img src="../../../assets/images/guided-tour/desktop/safari/AddBookmark.png" alt="Step 3" />
                    </div>
                  `;
        } else if (isChrome) {
          return `
                    <div>
                      <span style="text-align:left; color:#231F20; font-size: 20px; line-height: 24px;">Bookmark this app</span>
                      <br>
                      <span style="text-align:left; color:#231F20; font-weight: 400; font-size: 16px; line-height: 24px;">Bookmark this app for quick and easy access throughout the conference.</span>
                      <br>
                      <b>Step 1:</b> <i>Click on the star at the right side of the search bar</i>
                      <img src="../../../assets/images/guided-tour/desktop/chrome/navbar.png" alt="Step 1" />
                      <br>
                      <b>Step 2:</b> <i>Click on Done to add bookmark into your browser</i>
                      <img src="../../../assets/images/guided-tour/desktop/chrome/bookmark.png" alt="Step 2" />
                      <br>
                      <b>Step 3:</b> <i>Drag and drop the highlighted URL into your desktop screen"</i>
                    </div>
                  `;
        }
      }
      // If no condition matches, return the default image URL
      return '../../assets/images/guided-tour/mobile/chrome/';
    },
    isSignupClosed() {
      // needs refectoring (we are just using CG signup close as of now)
      const currentDate = this.currDate === '' ? new Date() : new Date(this.currDate);
      const signUpExpiry = new Date(this.SIGN_UP_CLOSE_TS);
      // console.log("signUpExpiry: " + signUpExpiry.getTime());
      return currentDate.getTime() >= signUpExpiry.getTime();
    },
    getExpAlloc() {
      momentts.tz.setDefault('Asia/Singapore');
      const dayStr = momentts(this.EXP_ALLOC_TS).format('dddd, D MMMM');
      const timeStr = momentts(this.EXP_ALLOC_TS).format('ha');
      return dayStr + ' at ' + timeStr;
    },
    hasSignupBefore() {
      let result = false;
      for (const expType in this.signups) {
        result = result || Boolean(this.signups[expType]);
      }
      return result;
    },
    signupCheck() {
      if (!this.isSignupClosed() && !this.hasSignupBefore()) {
        return true;
      }
      return false;
    },
    backToTop() {
      const elem = document.getElementById('content-home');
      elem.scrollToTop(1000);
    },
    showCustomComponent(name) {
      return !['cvp', 'schedule', 'experience', 'links'].includes(name);
    },
    showForDelegates(isPublic) {
      return isPublic || !this.isAbsentAlumni;
    },
    resetGuidedTour() {
      this.backToTop();

      setTimeout(() => this._pickAndLoadTour(), 100);
    },
    _pickAndLoadTour() {
      if (this.hasCompletedHomepageTour) return;
      if (this.type === 'SCS') {
        if (this.signupCheck()) {
          isNativeNotWeb() ? this.loadSCSNativeTour() : this.loadSCSTour();
        } else {
          isNativeNotWeb() ? this.loadSCSNoSignUpNativeTour() : this.loadSCSNoSignUpTour();
        }
      } else if (this.type === 'GC') {
        isNativeNotWeb() ? this.loadGCNativeTour() : this.loadGCTour();
      }
    },
    startHomeTour() {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: 'guided-tour-start-home',
          page: 'home',
        },
      });
      this.tour.next();
    },
    async _finishHomeTour(eventName) {
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: { button_name: eventName, page: 'home' },
      });
      const payload = {
        hasCompletedHomepageTour: true,
        conferenceType: CONFERENCE_TYPES.GLOBAL,
      };
      await this.$store.dispatch('profile/setHomeTourStatus', payload);
      await this.$store.dispatch('tour/setTour', null);
      this.backToTop();
      this.tour.cancel();
    },
    async skipHomeTour() {
      await this._finishHomeTour('guided-tour-skip-home');
    },
    async endHomeTour() {
      await this._finishHomeTour('guided-tour-end-home');
    },
    _shepherdConfig() {
      return {
        useModalOverlay: true,
        defaultStepOptions: {
          scrollTo: true,
          when: {
            show() {
              const currentStep = this.tour.getCurrentStep();
              const currentStepElement = currentStep?.getElement();
              const header = currentStepElement?.querySelector('.shepherd-header');
              const progress = document.createElement('span');
              progress.style['border-radius'] = '100px';
              progress.style['background'] = '#FFD900';
              progress.style['font-size'] = '12px';
              progress.style['font-weight'] = '500';
              progress.style['line-height'] = '20px';
              progress.style['color'] = '#2B2E79';
              progress.style['font-family'] = 'Arial';
              progress.style['width'] = '77px';
              progress.style['text-align'] = 'center';
              progress.className = 'shepherd-progress';
              progress.innerText = `Step ${this.tour.steps.indexOf(currentStep)} of ${
                this.tour.steps.length - 1
              }`;
              header?.insertBefore(
                progress,
                currentStepElement?.querySelector('.shepherd-cancel-icon'),
              );
            },
            cancel() {
              const payload = {
                hasCompletedHomepageTour: true,
                conferenceType: CONFERENCE_TYPES.GLOBAL,
              };
              store.dispatch('profile/setHomeTourStatus', payload);
              store.dispatch('tour/setTour', null);
            },
          },
          keyboardNavigation: false,
        },
      };
    },
    _welcomeStep(textKey) {
      return {
        id: 'welcome',
        attachTo: { on: 'top' },
        scrollTo: { behavior: 'smooth', block: 'start' },
        canClickTarget: false,
        buttons: [
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.skipButton'),
            action: this.skipHomeTour,
            secondary: true,
          },
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.startTour'),
            action: this.startHomeTour,
          },
        ],
        text: this.$t(textKey),
        when: {
          show() {
            const currentStep = this.tour.getCurrentStep();
            const currentStepElement = currentStep?.getElement();
            currentStepElement?.querySelector('.shepherd-content')?.classList.add('welcome-bg');
          },
        },
      };
    },
    _scheduleStep(scrollBlock) {
      return {
        id: 'schedule',
        attachTo: { element: '.schedule-section', on: 'bottom' },
        scrollTo: { behavior: 'smooth', block: scrollBlock },
        canClickTarget: false,
        buttons: [
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.backButton'),
            action: this.tour.back,
            secondary: true,
          },
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.nextButton'),
            action: this.tour.next,
          },
        ],
        highlightClass: 'highlight',
        text: this.$t('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep1'),
        cancelIcon: { enabled: true },
      };
    },
    _experiencesStep() {
      return {
        id: 'experiences',
        attachTo: { element: 'button#experienceIcon', on: 'bottom' },
        scrollTo: { behavior: 'smooth', block: 'center' },
        canClickTarget: false,
        text: this.$t('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep2'),
        buttons: [
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.backButton'),
            action: this.tour.back,
            secondary: true,
          },
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.nextButton'),
            action: this.tour.next,
          },
        ],
        cancelIcon: { enabled: true },
      };
    },
    _photodropStep() {
      return {
        id: 'photodrop',
        attachTo: { element: 'button#photodropIcon', on: 'bottom' },
        scrollTo: { behavior: 'smooth', block: 'center' },
        canClickTarget: false,
        buttons: [
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.backButton'),
            action: this.tour.back,
            secondary: true,
          },
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.endButton'),
            action: this.endHomeTour,
          },
        ],
        highlightClass: 'highlight',
        text: this.$t('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep3'),
        cancelIcon: { enabled: true },
      };
    },
    _postRegisterStep() {
      return {
        id: 'postRegister',
        buttons: [
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.backButton'),
            action: this.tour.back,
            secondary: true,
          },
          {
            text: this.$t('copy.homePage.mainLayout.appGuidedTour.endButton'),
            action: this.endHomeTour,
          },
        ],
        highlightClass: 'highlight',
        canClickTarget: false,
        text: this.addToBookmark(),
        cancelIcon: {
          enabled: true,
          label: this.$t('copy.homePage.mainLayout.appGuidedTour.skipTour'),
        },
      };
    },
    _startTour(buildSteps) {
      this.tour = this.$shepherd(this._shepherdConfig());
      this.tour.addSteps(buildSteps());
      this.tour.start();
      this.setTour(this.tour);
    },
    loadSCSTour() {
      this._startTour(() => [
        this._welcomeStep('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep0SCS'),
        this._scheduleStep('start'),
        this._experiencesStep(),
        this._photodropStep(),
        this._postRegisterStep(),
      ]);
    },
    loadSCSNativeTour() {
      this._startTour(() => [
        this._welcomeStep('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep0SCS'),
        this._scheduleStep('center'),
        this._experiencesStep(),
        this._photodropStep(),
      ]);
    },
    loadSCSNoSignUpTour() {
      this._startTour(() => [
        this._welcomeStep('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep0SCS'),
        this._scheduleStep('start'),
        this._photodropStep(),
        this._postRegisterStep(),
      ]);
    },
    loadSCSNoSignUpNativeTour() {
      this._startTour(() => [
        this._welcomeStep('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep0SCS'),
        this._scheduleStep('center'),
        this._photodropStep(),
      ]);
    },
    loadGCTour() {
      this._startTour(() => [
        this._welcomeStep('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep0GC'),
        this._scheduleStep('start'),
        this._photodropStep(),
        this._postRegisterStep(),
      ]);
    },
    loadGCNativeTour() {
      this._startTour(() => [
        this._welcomeStep('copy.homePage.mainLayout.appGuidedTour.homeTourTextStep0GC'),
        this._scheduleStep('center'),
        this._photodropStep(),
      ]);
    },

    triggerExpPopup(exp, slotProps) {
      slotProps.toggleExp(exp, false);
    },
    updateExperienceCarousel() {
      if (this.$refs.experienceSection && this.$refs.experienceSection.length > 0) {
        this.$refs.experienceSection[0].updateCarousel();
      }
    },
    async scrollToSlugSection() {
      const slug = this.$route.hash.slice(1);
      if (this.$refs[slug] && this.$refs[slug].length > 0) {
        await this.$refs[slug][0].scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
};
</script>

<style scoped>
.button {
  background: #fef8ec;
  height: 96px;
  width: 157.5px;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  margin-bottom: 4px;
  box-shadow: 0px 5px 5px #ccc;
  border-top: 1px solid #e9e9e9;
}

.home-hero {
  --home-action-size: 75px;
  --home-action-column-width: 75px;
  --home-action-gap: 29px;
  --home-label-size: 16px;
  --home-label-line-height: 24px;
  padding-bottom: clamp(12px, 3vw, 48px);
}

.home-hero-bg {
  height: clamp(194px, 54vw, 240px);
}

/* iOS gets extra banner height to absorb the notch padding (!pt-[60px] on the
   header); Android/web keep the original height so the curve doesn't sit low. */
.home-hero-bg-ios {
  height: clamp(200px, 80vw, 245px);
}

.home-hero-header {
  padding-top: clamp(40px, 10vw, 52px);
  padding-bottom: clamp(78px, 20vw, 100px);
}

.home-logo {
  height: clamp(40px, 10vw, 56px);
}

.home-header-actions {
  @apply flex items-center;
  gap: clamp(12px, 4vw, 18px);
}

.home-header-action-icon {
  width: 28px;
  height: 28px;
}

.home-actions {
  box-sizing: border-box;
  column-gap: var(--home-action-gap);
  width: 100%;
  padding-inline: 24px;
  margin-top: -60px;
}

.grid-cols {
  grid-template-columns: repeat(3, var(--home-action-column-width));
}

.grid-cols-GC {
  grid-template-columns: repeat(2, var(--home-action-column-width));
}

.home-action-item {
  @apply flex w-full flex-col items-center;
}

.home-action-button {
  @apply flex cursor-pointer items-center justify-center rounded-full hover:opacity-90;
  width: var(--home-action-size);
  height: var(--home-action-size);
}

.home-action-experience-icon {
  width: var(--home-action-size);
  height: var(--home-action-size);
}

.home-action-photodrop-icon {
  width: var(--home-action-size);
  height: var(--home-action-size);
}

.home-action-interpretation-icon {
  width: auto;
  height: calc(var(--home-action-size) * 0.7227);
}

.home-action-label {
  @apply text-center font-aktiv-grotesk-trial font-semibold text-grey-100;
  min-width: var(--home-action-size);
  width: max-content;
  margin-top: clamp(6.4px, 1.92vw, 8.96px);
  font-size: var(--home-label-size);
  line-height: var(--home-label-line-height);
  white-space: nowrap;
}

.icon-shadow {
  filter: drop-shadow(0px 0.94px 9.36px rgba(0, 0, 0, 0.1));
}

.unread-count {
  background: #ff6600;
  text-align: center;
  font-size: 12.8px;
  font-weight: 600;
  line-height: clamp(17.6px, 4.8vw, 22.4px);
  color: #fef8ec;
  position: absolute;
  top: -4px;
  right: -4px;
  width: clamp(17.6px, 4.8vw, 22.4px);
  height: clamp(17.6px, 4.8vw, 22.4px);
  border-radius: 50%;
  transform: translateY(-20%);
}

h2 {
  @apply text-[32px] leading-[48px] font-aktiv-grotesk-medium text-blue;
}

@media all and (min-width: 768px) {
  .home-hero {
    --home-action-size: 96px;
    --home-action-column-width: 115px;
    --home-action-gap: 82px;
  }

  .home-actions {
    padding-inline: 24px;
    margin-top: calc(var(--home-action-size) * -0.88);
  }

  /* keep the curve at the same position relative to the larger action icons */
  .home-hero-bg {
    height: 300px;
  }

  .unread-count {
    top: -4px;
    right: -4px;
  }

  .show {
    display: block;
  }
}

@media all and (min-width: 375px) and (max-width: 767px) {
  .home-hero {
    --home-action-gap: 45px;
  }
}

@media all and (min-width: 1024px) {
  .home-actions {
    padding-inline: 54px;
    margin-top: calc(var(--home-action-size) * -0.94);
  }

  .home-hero-bg {
    height: 292px;
  }
}
</style>
